/**
 * The Devices tab's map view.
 *
 * Read-only on purpose. Placing and correcting a pin is the tech's job
 * standing at the assembly — `components/location-picker-map.tsx` already does
 * that on the field screen, and an office user dragging pins from a desk would
 * be guessing with more confidence than the tech who was there.
 *
 * Reuses that component's tile constants rather than re-declaring them, so the
 * office and the field are looking at the same imagery.
 *
 * All the judgement lives in `device-map.ts`; this file draws what it is told.
 */
import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import { Link } from "react-router-dom";
import { MapPinOff } from "lucide-react";
import { ensureLeafletCSS } from "@/components/location-picker-map";
import { hasAnythingToMap, mapCentre, mapCoverageNote, pinTone, splitByPin, type MappableDevice } from "./device-map";
import { addBasemapLayer } from "@/lib/map-tiles";

/** Matches the compliance card's tones so one page speaks one language. */
const TONE_COLOR: Record<ReturnType<typeof pinTone>, string> = {
    overdue: "#dc2626",
    missing_due: "#d97706",
    ok: "#059669",
};

function markerFor(d: MappableDevice, confirmed: boolean, today: Date): L.CircleMarker {
    const color = TONE_COLOR[pinTone(d, today)];
    return L.circleMarker([Number(d.location_lat), Number(d.location_lng)], {
        radius: confirmed ? 9 : 7,
        color,
        weight: 2,
        // An UNSET pin is the building centroid, not the assembly. Hollow and
        // dashed so it cannot be mistaken for a placed one at a glance — the
        // legend says the same thing in words for anyone who doesn't read
        // outlines as meaning.
        fillColor: confirmed ? color : "transparent",
        fillOpacity: confirmed ? 0.85 : 0,
        dashArray: confirmed ? undefined : "3 3",
    });
}

export function DeviceMapView({
    devices,
    site,
}: {
    devices: readonly MappableDevice[];
    site: { latitude?: number | string | null; longitude?: number | string | null };
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const layerRef = useRef<L.LayerGroup | null>(null);

    const split = useMemo(() => splitByPin(devices), [devices]);
    const centre = useMemo(
        () => mapCentre(split, { lat: site.latitude, lng: site.longitude }),
        [split, site.latitude, site.longitude],
    );
    const note = mapCoverageNote(split);
    const drawable = hasAnythingToMap(split);

    useEffect(() => {
        if (!drawable || !centre || !containerRef.current) return;
        ensureLeafletCSS();
        if (!mapRef.current) {
            mapRef.current = L.map(containerRef.current, { attributionControl: true });
            addBasemapLayer(L, mapRef.current, "satellite", { maxZoom: 22 });
            layerRef.current = L.layerGroup().addTo(mapRef.current);
        }
        const map = mapRef.current;
        const layer = layerRef.current!;
        layer.clearLayers();

        const today = new Date();
        const bounds: L.LatLngExpression[] = [];
        for (const [list, confirmed] of [
            [split.confirmed, true],
            [split.approximate, false],
        ] as const) {
            for (const d of list) {
                const m = markerFor(d, confirmed, today);
                m.bindPopup(
                    `<strong>${d.name ?? "Device"}</strong>` +
                        (d.serial_number ? `<br/>${d.serial_number}` : "") +
                        (confirmed ? "" : "<br/><em>Shown at the building — not placed</em>"),
                );
                m.addTo(layer);
                bounds.push([Number(d.location_lat), Number(d.location_lng)]);
            }
        }
        if (bounds.length > 1) map.fitBounds(L.latLngBounds(bounds).pad(0.25), { maxZoom: 20 });
        else map.setView([centre.lat, centre.lng], 19);

        // Leaflet measures the container on init; inside a tab that was hidden
        // it measures zero and renders a grey box until something resizes.
        setTimeout(() => map.invalidateSize(), 0);
    }, [drawable, centre, split]);

    useEffect(
        () => () => {
            mapRef.current?.remove();
            mapRef.current = null;
        },
        [],
    );

    return (
        <div className="space-y-3">
            {drawable && centre ? (
                <div ref={containerRef} className="h-[420px] w-full overflow-hidden rounded-lg border border-border" />
            ) : (
                <div className="flex h-[220px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card text-center">
                    <MapPinOff className="h-6 w-6 text-foreground/50" />
                    <p className="text-sm text-foreground/75">
                        No device on this location has a position on record yet.
                    </p>
                    <p className="max-w-sm text-xs text-foreground/60">
                        A pin is placed by the tech standing at the assembly, so these fill in as the location gets
                        worked.
                    </p>
                </div>
            )}

            {/* The coverage sentence is not optional chrome. A map that shows
                4 of 12 devices and says nothing reads as complete, and the
                office would count the pins and believe it. */}
            <p className="text-sm text-foreground/80">{note}</p>

            {drawable && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground/70">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full border-2 border-foreground/60 bg-foreground/60" />
                        Placed by a tech
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full border-2 border-dashed border-foreground/60" />
                        At the building — not placed
                    </span>
                    <span className="ml-auto inline-flex items-center gap-3">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full bg-destructive" /> Overdue
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full bg-warning" /> No due date
                        </span>
                    </span>
                </div>
            )}

            {split.unplaced.length > 0 && (
                // Listed, never dropped. This list IS the answer to "why are
                // there only four pins".
                <div className="rounded-lg border border-border bg-card p-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground/70">
                        Not on the map ({split.unplaced.length})
                    </p>
                    <ul className="flex flex-wrap gap-x-4 gap-y-1">
                        {split.unplaced.map((d) => (
                            <li key={d.id}>
                                <Link to={`/assets/${d.id}`} className="text-sm text-primary hover:underline">
                                    {d.serial_number?.trim() || d.name || "Device"}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
